import * as React from 'react';

import debounce from 'lodash/debounce';

type State = {
  isElementInViewport?: boolean;
  elementNotFound?: boolean;
};

export type CheckElementInViewportConfig = {
  selector: string;
  inViewportStartingValue: boolean;
  // Between 0 and 1. 1 being the element must be 100% visible in order to trigger state change.
  intersectionObserverThreshold?: number;
};

// Pass a valid querySelector string or make sure Component using this HOC has a prop called 'elementToTrackForViewPortVisibility' and track whether that element is present or not in the viewport
// Returns boolean prop 'isElementInViewport'
const withCheckElementInViewport =
  (selector?: string, inViewportStartingValue?: boolean, intersectionObserverThreshold?: number) =>
  <Props extends { checkElementInViewportConfig?: CheckElementInViewportConfig }>(
    Component: React.ComponentType<Props>
  ) => {
    return class extends React.Component<Props, State> {
      element: HTMLElement | null;

      state = {
        isElementInViewport:
          inViewportStartingValue || this.props.checkElementInViewportConfig?.inViewportStartingValue || false,
        elementNotFound: false,
      };

      constructor(props: Props) {
        super(props);
        this.element = null;
      }

      componentDidMount() {
        const elementSelector = selector || this.props.checkElementInViewportConfig?.selector;
        if (elementSelector) {
          this.element = window.document.querySelector(elementSelector);

          // There are edge cases on client-side transition where data is loading and element is not on the page.
          // Notifies wrapped components that this happened so they can handle accordingly.
          if (!this.element) {
            this.setState({
              elementNotFound: true,
            });
          }

          if ('IntersectionObserver' in window) {
            const observer = this.createIntersectionObserver();
            if (this.element) {
              observer.observe(this.element);
            }
          } else {
            // Fallback to window scroll listener
            // @ts-expect-error TSMIGRATION-4.9 TS2339
            window.addEventListener('scroll', debounce(this.handleScrollEventListener, 100, { leading: true }));
          }
        }
      }

      componentWillUnmount() {
        if (selector) {
          window.removeEventListener('scroll', debounce(this.handleScrollEventListener, 100, { leading: true }));
        }
      }

      handleScrollEventListener = () => {
        if (!this.element) {
          return null;
        }

        const { top, left } = this.element.getBoundingClientRect();

        // Check if element is visible within the viewport
        const isVisible =
          top < window.pageYOffset + window.innerHeight &&
          left < window.pageXOffset + window.innerWidth &&
          top + this.element.offsetHeight > window.pageYOffset &&
          left + this.element.offsetWidth > window.pageXOffset;

        if (isVisible) {
          this.setIsElementInViewport(true);
        } else {
          this.setIsElementInViewport(false);
        }
      };

      setIsElementInViewport = (inViewport: boolean) => {
        this.setState(() => ({
          isElementInViewport: inViewport,
        }));
      };

      createIntersectionObserver = () => {
        const options = {
          root: null,
          rootMargin: '0px',
          threshold: intersectionObserverThreshold || 1.0,
        };

        return new IntersectionObserver(([entry]) => {
          if (entry.isIntersecting) {
            this.setIsElementInViewport(true);
          } else {
            this.setIsElementInViewport(false);
          }
        }, options);
      };

      render() {
        const { isElementInViewport, elementNotFound } = this.state;

        return <Component {...{ ...this.props, isElementInViewport, elementNotFound }} />;
      }
    };
  };

export default withCheckElementInViewport;
